import { useHistory } from 'react-router-dom';

import { Anchor, Avatar, Button, Card, Divider, Heading, List, MediaObject, Paragraph } from '@zillow/constellation-10';
import { LoadingButton, LoadingHeading, LoadingText } from '@zg-rentals/loading-components';

import IdentityContentBox from '../../components/Widgets/IdentityContentBox';
import { Lease } from '../../models';
import { FULLSTORY_EXCLUDE } from '../../constants/analytics';
import { ZILLOW_TERMS_OF_USE_LINKS } from '../../constants/stringConstants';
import { useInfoForUser, useLeaseFromOuidQuery } from '../../utils/queries';
import NoAccessLeaseAlert from '../../components/Widgets/NoAccessLeaseAlert';

export function ColandlordWelcome() {
  const queryParameters = new URLSearchParams(window.location.search);
  const ouid = queryParameters.get('ouid');
  const infoForUserQuery = useInfoForUser(ouid, !!ouid);
  const leaseFromOuidQuery = useLeaseFromOuidQuery(ouid, !!ouid);
  const isSelfUpload = leaseFromOuidQuery?.data?.isSelfUpload;

  const history = useHistory();

  function onViewLeaseSummary() {
    history.push(
      isSelfUpload
        ? Lease.getSelfServePath(String(infoForUserQuery?.data?.leaseId ?? ''))
        : Lease.getSummaryPath(String(infoForUserQuery?.data?.leaseId ?? '')),
    );
  }

  const isLoading = infoForUserQuery.isLoading || leaseFromOuidQuery.isLoading;

  if (isLoading) {
    return (
      <>
        <LoadingHeading
          isLoading
          level={2}
          loadingWidth="60%"
          css={{
            textStyle:
              'responsive.heading-xs' /* eslint-disable-line c11nLint/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
            marginBottom: 'tighter',
          }}
        >
          Loading
        </LoadingHeading>
        <LoadingText isLoading loadingWidth="20%" css={{ wordBreak: 'break-word' }}>
          Loading
        </LoadingText>
        <LoadingText isLoading loadingWidth="80%" css={{ marginBottom: 'tight' }}>
          Loading
        </LoadingText>
        <Card tone="neutral" elevated={false} css={{ marginBottom: 'tight' }}>
          <LoadingHeading
            isLoading
            level={3}
            loadingWidth="50%"
            css={{
              textStyle:
                'responsive.heading-xs' /* eslint-disable-line c11nLint/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
            }}
          >
            Loading
          </LoadingHeading>
          <LoadingText isLoading loadingWidth="90%" css={{ marginBottom: 'tight' }}>
            Loading
          </LoadingText>
          <LoadingText isLoading loadingWidth="95%" css={{ marginBottom: 'tight' }}>
            Loading
          </LoadingText>
          <LoadingText isLoading loadingWidth="100%" css={{ marginBottom: 'tight' }}>
            Loading
          </LoadingText>
          <LoadingText isLoading loadingWidth="85%">
            Loading
          </LoadingText>
        </Card>
        <MediaObject media={<Avatar size="lg" />}>
          <LoadingText isLoading loadingWidth="60%" css={{ marginBottom: 'tight' }}>
            Loading
          </LoadingText>
          <LoadingText isLoading loadingWidth="90%" css={{ marginBottom: 'tighter' }}>
            Loading
          </LoadingText>
        </MediaObject>
        <Divider css={{ marginBottom: 'tight' }} />
        <LoadingText isLoading loadingWidth="80%" css={{ marginBottom: 'tight' }}>
          Loading
        </LoadingText>
        <LoadingButton isLoading loadingWidth="100%">
          Loading
        </LoadingButton>
      </>
    );
  }

  return (
    <>
      {!ouid || infoForUserQuery.isError || leaseFromOuidQuery.isError ? (
        <NoAccessLeaseAlert />
      ) : (
        <>
          <Heading
            level={2}
            css={{
              textStyle:
                'responsive.heading-xs' /* eslint-disable-line c11nLint/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
              marginBottom: 'tighter',
            }}
          >
            Your lease is ready to sign
          </Heading>
          <Paragraph className={FULLSTORY_EXCLUDE} css={{ wordBreak: 'break-word' }}>
            Hi {infoForUserQuery?.data?.firstName},
          </Paragraph>
          <Paragraph css={{ marginBottom: 'tight' }}>
            We’re so glad you’re here. Together with your co-landlord, we’ll help you review and sign your lease.
          </Paragraph>
          <Card tone="soft" elevated={false} css={{ marginBottom: 'tight' }}>
            <Heading
              level={3}
              css={{
                textStyle:
                  'responsive.heading-xs' /* eslint-disable-line c11nLint/no-unknown-token -- valid textStyle; rule can't check textStyle (JSINFRA-3580) */,
              }}
            >
              How online leases work
            </Heading>
            <List.Root>
              <List.Item>
                Review the lease, and ask your co-landlord to change anything that doesn’t seem right
              </List.Item>
              <List.Item>
                Your co-landlord revises the lease and arranges for your tenants to sign it
                {isSelfUpload ? ' online' : ', either online or on paper'}
              </List.Item>
              <List.Item>
                Once your tenants sign the lease, we’ll let you know that it’s ready for you to countersign’
              </List.Item>
              <List.Item>The lease is ratified when you and your co-landlord countersign</List.Item>
            </List.Root>
          </Card>
          <IdentityContentBox
            firstName={infoForUserQuery?.data?.firstName}
            lastName={infoForUserQuery?.data?.lastName}
            email={infoForUserQuery?.data?.email}
          >
            <Paragraph css={{ textStyle: 'body-sm', marginBottom: 'tighter' }}>
              By <strong>continuing to view your lease</strong>, you agree to comply with the{' '}
              <Anchor target="_blank" rel="noopener noreferrer" href={ZILLOW_TERMS_OF_USE_LINKS.TERMS_OF_USE}>
                Terms of Use
              </Anchor>{' '}
              and the{' '}
              <Anchor target="_blank" rel="noopener noreferrer" href={ZILLOW_TERMS_OF_USE_LINKS.RENTER_TERMS_OF_USE}>
                Rental User Terms
              </Anchor>
            </Paragraph>
            <Button tone="brand" emphasis="filled" fluid onClick={onViewLeaseSummary}>
              Continue to view your lease
            </Button>
          </IdentityContentBox>
        </>
      )}
    </>
  );
}
